'use client';

import { useState, useEffect } from 'react';
import { useScrollReveal } from './useScrollReveal';
import { uiFb } from '@/lib/i18n';
import { formatDateRangeLocale } from '@/lib/date-format';

function getValueProps(lang: string) {
  return [
    {
      accent: 'var(--event-national)',
      icon: '\u{1F4C5}',
      title: uiFb('ui.vp_title_1', 'Know before you go', lang),
      body: null, // Custom render for closure-impact card
    },
    {
      accent: 'var(--event-festival)',
      icon: '\u{1F3AD}',
      title: uiFb('ui.vp_title_2', 'Understand cultural moments', lang),
      body: uiFb('ui.vp_body_2', 'Festivals, religious observances, and celebrations explained with local context \u2014 not just dates.', lang),
    },
    {
      accent: 'var(--event-observance)',
      icon: '\u{1F30D}',
      title: uiFb('ui.vp_title_3', 'One calendar, 14 countries', lang),
      body: uiFb('ui.vp_body_3', 'Indonesia, India, Malaysia, and 10 more \u2014 in one place, in your language. Save, share, or bookmark \u2014 always one click away.', lang),
    },
  ];
}

const CARD_DELAYS = ['delay-200', 'delay-300', 'delay-400'];

// #3 — Rotating closure impact examples
function getClosureExamples(lang: string) {
  return [
    {
      name: uiFb('ui.vp_closure_name_1', 'Eid al-Fitr', lang),
      badge: uiFb('ui.vp_closure_badge_national', 'National Holiday', lang),
      date: formatDateRangeLocale('2026-03-30', '2026-04-01', lang),
      closed: uiFb('ui.vp_closure_closed_1', 'Shops & services', lang),
      isFriendly: true,
      friendly: uiFb('ui.vp_closure_friendly_yes', 'Visitor-friendly: Yes', lang),
      bookAhead: uiFb('ui.vp_closure_book_14', '14 days', lang),
    },
    {
      name: uiFb('ui.vp_closure_name_2', 'Nyepi (Day of Silence)', lang),
      badge: uiFb('ui.vp_closure_badge_hindu', 'Hindu Observance', lang),
      date: formatDateRangeLocale('2026-03-19', '2026-03-19', lang),
      closed: uiFb('ui.vp_closure_closed_2', 'Airport & roads', lang),
      isFriendly: false,
      friendly: uiFb('ui.vp_closure_friendly_no', 'Visitor-friendly: No', lang),
      bookAhead: uiFb('ui.vp_closure_book_30', '30 days', lang),
    },
    {
      name: uiFb('ui.vp_closure_name_3', 'Diwali', lang),
      badge: uiFb('ui.vp_closure_badge_national', 'National Holiday', lang),
      date: formatDateRangeLocale('2026-10-20', '2026-10-20', lang),
      closed: uiFb('ui.vp_closure_closed_3', 'Banks & offices', lang),
      isFriendly: true,
      friendly: uiFb('ui.vp_closure_friendly_yes', 'Visitor-friendly: Yes', lang),
      bookAhead: uiFb('ui.vp_closure_book_7', '7 days', lang),
    },
    {
      name: uiFb('ui.vp_closure_name_4', 'Songkran', lang),
      badge: uiFb('ui.vp_closure_badge_cultural', 'Cultural Festival', lang),
      date: formatDateRangeLocale('2026-04-13', '2026-04-15', lang),
      closed: uiFb('ui.vp_closure_closed_4', 'Government offices', lang),
      isFriendly: true,
      friendly: uiFb('ui.vp_closure_friendly_yes', 'Visitor-friendly: Yes', lang),
      bookAhead: uiFb('ui.vp_closure_book_21', '21 days', lang),
    },
  ];
}

function ClosureImpactCard({ lang = 'en' }: { lang?: string }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const closureExamples = getClosureExamples(lang);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % closureExamples.length);
        setVisible(true);
      }, 280);
    }, 4500);
    return () => clearInterval(id);
  }, [closureExamples.length]);

  const ex = closureExamples[index];
  const fadeStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(4px)',
    transition: 'all 280ms ease',
  };

  return (
    <div className="mt-3 space-y-2" style={fadeStyle}>
      {/* Mini event card */}
      <div className="content-card-elevated p-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">{ex.name}</span>
          <span className="rounded-full bg-brand/10 px-2 py-0.5 text-caption font-semibold text-brand">{ex.badge}</span>
        </div>
        <p className="mt-1 text-xs text-muted">{ex.date}</p>
      </div>

      {/* Impact badges */}
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-semantic-error/10 px-2.5 py-1 text-label font-semibold text-semantic-error">
          <span className="h-1.5 w-1.5 rounded-full bg-semantic-error" />
          {uiFb('ui.vp_closure_closed_label', 'Closed:', lang)} {ex.closed}
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-label font-semibold ${
          ex.isFriendly
            ? 'bg-semantic-success/10 text-semantic-success'
            : 'bg-semantic-error/10 text-semantic-error'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${
            ex.isFriendly ? 'bg-semantic-success' : 'bg-semantic-error'
          }`} />
          {ex.friendly}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-semantic-warning/10 px-2.5 py-1 text-label font-semibold text-semantic-warning">
          <span className="h-1.5 w-1.5 rounded-full bg-semantic-warning" />
          {uiFb('ui.vp_closure_book_label', 'Book ahead:', lang)} {ex.bookAhead}
        </span>
      </div>
    </div>
  );
}

export function LandingValueProps({ lang = 'en' }: { lang?: string }) {
  const { ref, revealed } = useScrollReveal();
  const valueProps = getValueProps(lang);

  return (
    <section ref={ref} className="px-4 py-8 md:px-8 md:py-12 lg:px-16 xl:px-20">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <h2
          className={`scroll-reveal ${revealed ? 'revealed' : ''} text-center font-heading text-2xl font-bold tracking-tight text-text-primary md:text-4xl`}
        >
          {uiFb('ui.vp_heading', 'Why a world calendar, not just a date lookup?', lang)}
        </h2>

        {/* 3 value-prop cards */}
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {valueProps.map((point, i) => (
            <div
              key={point.title}
              className={`scroll-reveal ${revealed ? 'revealed' : ''} ${CARD_DELAYS[i]} flex flex-col overflow-hidden rounded-xl border border-border-subtle bg-surface-card`}
            >
              <div className="h-1 shrink-0" style={{ backgroundColor: point.accent }} />
              <div className="flex flex-1 flex-col p-5 md:p-6">
                <div className="text-2xl" role="img" aria-label={point.title}>{point.icon}</div>
                <h3 className="mt-3 font-heading text-base font-bold text-text-primary md:text-lg">
                  {point.title}
                </h3>
                {point.body ? (
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-text-secondary">
                    {point.body}
                  </p>
                ) : (
                  <ClosureImpactCard lang={lang} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Solution pivot */}
        <p
          className={`scroll-reveal ${revealed ? 'revealed' : ''} delay-600 mx-auto mt-10 max-w-2xl text-center text-base font-medium text-brand md:text-lg`}
        >
          {uiFb('ui.vp_pivot', 'Whether you\'re planning a trip to Bali, checking school holidays, or tracking Ramadan dates \u2014 vacant.day has you covered.', lang)}
        </p>
      </div>
    </section>
  );
}
